import { useCallback, useMemo } from 'react';
import { ProductFilter, ProductTag } from '../../../types/product.types';
import { useProductMetadata } from '../../../hooks';
import { useRangeFilter } from '../../../hooks/useRangeFilter';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UseProductFiltersProps {
  filters: ProductFilter;
  setFilters: React.Dispatch<React.SetStateAction<ProductFilter>>;
  appliedFilters: ProductFilter;
  onClearAppliedField: (patch: Partial<ProductFilter>) => void;
  showMoreFilters: boolean;
  onSearch: () => void;
  onReset: () => void;
  onToggleMoreFilters: () => void;
  productTypes: { id: number; name: string }[];
  suppliers: { id: number; name: string }[];
  productTags: ProductTag[];
  materials?: { id: number; name: string }[];
}

export const useProductFilters = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  onSearch,
  onReset,
  onToggleMoreFilters,
  productTypes,
  suppliers,
  productTags,
  materials = [],
}: UseProductFiltersProps) => {
  const { tags: sharedTags } = useProductMetadata();
  const safeProductTypes = useMemo(
    () => (Array.isArray(productTypes) ? productTypes : []),
    [productTypes],
  );
  const safeSuppliers = useMemo(() => (Array.isArray(suppliers) ? suppliers : []), [suppliers]);
  const safeProductTags = useMemo(
    () => (Array.isArray(productTags) ? productTags : sharedTags),
    [productTags, sharedTags],
  );
  const safeMaterials = useMemo(() => (Array.isArray(materials) ? materials : []), [materials]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [setFilters],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSearch();
      }
    },
    [onSearch],
  );

  const renderProductTypeValue = useCallback(
    (selected: unknown) => {
      if (!selected) return <span style={{ color: 'gray' }}>Seleccione</span>;
      const productType = safeProductTypes.find((type) => type.id.toString() === selected);
      return productType?.name || '';
    },
    [safeProductTypes],
  );

  const renderStatusValue = useCallback((selected: unknown) => {
    if (!selected) return '';
    return selected === 'ACTIVE' ? 'Activo' : 'Inactivo';
  }, []);

  const renderSupplierValue = useCallback(
    (selected: unknown) => {
      if (!selected) return <span style={{ color: 'gray' }}>Seleccione</span>;
      const supplier = safeSuppliers.find((s) => s.id === selected);
      return supplier?.name || '';
    },
    [safeSuppliers],
  );

  // Autocomplete handlers
  const handleProductTypeChange = useCallback(
    (event: any, newValue: any) => {
      setFilters((prev) => ({ ...prev, productType: newValue ? newValue.id.toString() : '' }));
    },
    [setFilters],
  );

  const handleSupplierChange = useCallback(
    (event: any, newValue: any) => {
      setFilters((prev) => ({ ...prev, supplierId: newValue ? newValue.id.toString() : '' }));
    },
    [setFilters],
  );

  const handleTagsChange = useCallback(
    (event: any, newValue: any) => {
      const tagNames = newValue.map((tag: any) => tag.name).join(',');
      setFilters((prev) => ({ ...prev, searchTag: tagNames }));
    },
    [setFilters],
  );

  const handleMaterialChange = useCallback(
    (event: any, newValue: any) => {
      setFilters((prev) => ({ ...prev, materialId: newValue ? newValue.id.toString() : '' }));
    },
    [setFilters],
  );

  // Get current values for Autocomplete
  const selectedProductType = useMemo(() => {
    return safeProductTypes.find((type) => type.id.toString() === filters.productType) || null;
  }, [safeProductTypes, filters.productType]);

  const selectedSupplier = useMemo(() => {
    return safeSuppliers.find((supplier) => supplier.id.toString() === filters.supplierId) || null;
  }, [safeSuppliers, filters.supplierId]);

  const selectedTags = useMemo(() => {
    if (!filters.searchTag) return [];
    const tagNames = filters.searchTag
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name);
    return safeProductTags.filter((tag) => tagNames.includes(tag.name));
  }, [safeProductTags, filters.searchTag]);

  const selectedMaterial = useMemo(() => {
    return safeMaterials.find((material) => material.id.toString() === filters.materialId) || null;
  }, [safeMaterials, filters.materialId]);

  const productTypeMenuItems = useMemo(
    () =>
      safeProductTypes.map((type) => ({
        key: `type-${type.id}`,
        value: type.id.toString(),
        label: type.name,
      })),
    [safeProductTypes],
  );
  const supplierMenuItems = useMemo(
    () => safeSuppliers.map((s) => ({ key: `supplier-${s.id}`, value: s.id, label: s.name })),
    [safeSuppliers],
  );

  const removeCommaValue = useCallback((csv: string | undefined, value: string) => {
    return (csv || '')
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part && part !== value)
      .join(',');
  }, []);

  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const chips: ListPageActiveFilterChip[] = [];
    const applied = appliedFilters;

    if (applied.searchTerm) {
      chips.push({
        key: 'searchTerm',
        label: `Búsqueda: ${applied.searchTerm}`,
        onRemove: () => onClearAppliedField({ searchTerm: '' }),
      });
    }
    if (applied.productType) {
      const typeName =
        safeProductTypes.find((t) => t.id.toString() === applied.productType)?.name ||
        applied.productType;
      chips.push({
        key: 'productType',
        label: `Tipo: ${typeName}`,
        onRemove: () => onClearAppliedField({ productType: '' }),
      });
    }
    if (applied.productStatus) {
      const statusLabel = applied.productStatus === 'ACTIVE' ? 'Activo' : 'Inactivo';
      chips.push({
        key: 'productStatus',
        label: `Estado: ${statusLabel}`,
        onRemove: () => onClearAppliedField({ productStatus: '' }),
      });
    }
    if (applied.supplierId) {
      const supplierName =
        safeSuppliers.find((s) => s.id.toString() === applied.supplierId)?.name ||
        applied.supplierId;
      chips.push({
        key: 'supplierId',
        label: `Proveedor: ${supplierName}`,
        onRemove: () => onClearAppliedField({ supplierId: '' }),
      });
    }
    (applied.searchTag || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((tag) => {
        chips.push({
          key: `searchTag:${tag}`,
          label: `Etiqueta: ${tag}`,
          onRemove: () =>
            onClearAppliedField({ searchTag: removeCommaValue(applied.searchTag, tag) }),
        });
      });
    if (applied.materialId) {
      const matName =
        safeMaterials.find((m) => m.id.toString() === applied.materialId)?.name ||
        applied.materialId;
      chips.push({
        key: 'materialId',
        label: `Material: ${matName}`,
        onRemove: () => onClearAppliedField({ materialId: '' }),
      });
    }
    if (applied.diameterSize) {
      chips.push({
        key: 'diameterSize',
        label: `Talla: ${applied.diameterSize}`,
        onRemove: () => onClearAppliedField({ diameterSize: '' }),
      });
    }
    if (applied.color) {
      chips.push({
        key: 'color',
        label: `Color: ${applied.color}`,
        onRemove: () => onClearAppliedField({ color: '' }),
      });
    }
    if (applied.workshopCode) {
      chips.push({
        key: 'workshopCode',
        label: `Código proveedor: ${applied.workshopCode}`,
        onRemove: () => onClearAppliedField({ workshopCode: '' }),
      });
    }
    if (applied.minWeight != null) {
      chips.push({
        key: 'minWeight',
        label: `Peso min: ${applied.minWeight}g`,
        onRemove: () => onClearAppliedField({ minWeight: undefined }),
      });
    }
    if (applied.maxWeight != null) {
      chips.push({
        key: 'maxWeight',
        label: `Peso max: ${applied.maxWeight}g`,
        onRemove: () => onClearAppliedField({ maxWeight: undefined }),
      });
    }

    return chips;
  }, [
    appliedFilters,
    safeProductTypes,
    safeSuppliers,
    safeMaterials,
    onClearAppliedField,
    removeCommaValue,
  ]);

  return {
    filters,
    showMoreFilters,
    handleInputChange,
    handleKeyPress,
    renderProductTypeValue,
    renderStatusValue,
    renderSupplierValue,
    productTypeMenuItems,
    supplierMenuItems,
    onSearch,
    onReset,
    onToggleMoreFilters,
    setFilters,
    // New Autocomplete handlers
    handleProductTypeChange,
    handleSupplierChange,
    handleTagsChange,
    handleMaterialChange,
    selectedProductType,
    selectedSupplier,
    selectedTags,
    selectedMaterial,
    safeProductTypes,
    safeSuppliers,
    safeProductTags,
    safeMaterials,
    activeFilterChips,
  };
};

const DEFAULT_WEIGHT_MAX = 100;
const ABSOLUTE_WEIGHT_MAX = 1000;

export const useWeightSlider = (
  minWeight: number | undefined,
  maxWeight: number | undefined,
  setFilters: React.Dispatch<React.SetStateAction<ProductFilter>>,
) => {
  const onWeightPatch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) => {
      setFilters((prev) => ({
        ...prev,
        minWeight: patch.min,
        maxWeight: patch.max,
      }));
    },
    [setFilters],
  );

  const range = useRangeFilter({
    min: minWeight,
    max: maxWeight,
    defaultMax: DEFAULT_WEIGHT_MAX,
    absoluteMax: ABSOLUTE_WEIGHT_MAX,
    onPatch: onWeightPatch,
    onSearchWithPatch: onWeightPatch,
    onSliderCommit: onWeightPatch,
  });

  return {
    tempValues: range.sliderTemp,
    sliderMax: range.sliderAxisMax,
    handleSliderChange: range.onSliderChange,
    handleSliderCommitted: range.onSliderCommitted,
    handleMinWeightChange: (value: number | undefined) => range.applyMin(value, false),
    handleMaxWeightChange: (value: number | undefined) => range.applyMax(value, false),
    applyMinWeight: range.applyMin,
    applyMaxWeight: range.applyMax,
  };
};
